export function registerServiceWorker(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((error) => {
      console.warn("Service worker registration failed", error);
    });
  });
}

type PwaInstallOutcome = "accepted" | "dismissed" | "unavailable" | "error" | "unknown";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<unknown>;
  userChoice?: Promise<{ outcome?: PwaInstallOutcome }>;
};

export class PwaInstallController {
  private button: HTMLButtonElement | null = null;
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly displayModeQuery = typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(display-mode: standalone)")
    : null;

  constructor(private readonly onPromptOutcome?: (outcome: PwaInstallOutcome) => void) {
    if (typeof window === "undefined") {
      return;
    }

    window.addEventListener("beforeinstallprompt", this.handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", this.handleAppInstalled);
    this.displayModeQuery?.addEventListener?.("change", this.syncButton);
  }

  attachButton(button: HTMLButtonElement | null): void {
    if (this.button) {
      this.button.removeEventListener("click", this.handleButtonClick);
    }
    this.button = button;
    this.button?.addEventListener("click", this.handleButtonClick);
    this.syncButton();
  }

  private readonly handleBeforeInstallPrompt = (event: Event): void => {
    const installPrompt = event as BeforeInstallPromptEvent;
    if (typeof installPrompt.prompt !== "function" || this.isPwaDisplayMode()) {
      return;
    }

    event.preventDefault();
    this.deferredPrompt = installPrompt;
    this.syncButton();
  };

  private readonly handleAppInstalled = (): void => {
    this.deferredPrompt = null;
    this.syncButton();
  };

  private readonly handleButtonClick = (): void => {
    void this.promptInstall();
  };

  private async promptInstall(): Promise<void> {
    if (!this.deferredPrompt || this.isPwaDisplayMode()) {
      this.onPromptOutcome?.("unavailable");
      this.syncButton();
      return;
    }

    const installPrompt = this.deferredPrompt;
    this.deferredPrompt = null;
    if (this.button) this.button.disabled = true;
    this.syncButton();

    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice?.catch(() => undefined);
      this.onPromptOutcome?.(choice?.outcome ?? "unknown");
    } catch (error) {
      console.warn("PWA install prompt failed", error);
      this.onPromptOutcome?.("error");
    } finally {
      if (this.button) this.button.disabled = false;
      this.syncButton();
    }
  }

  private readonly syncButton = (): void => {
    if (!this.button) {
      return;
    }
    this.button.hidden = !this.deferredPrompt || this.isPwaDisplayMode();
  };

  private isPwaDisplayMode(): boolean {
    return Boolean(
      this.displayModeQuery?.matches ||
        window.matchMedia?.("(display-mode: fullscreen)")?.matches ||
        window.matchMedia?.("(display-mode: minimal-ui)")?.matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone
    );
  }
}
